<template>
    <div class="my-2 mx-2 w-full">
        <div class="flex flex-wrap gap-3">
            <!-- Card: Marca existente -->
            <div
                v-for="(item, index) in itemsCurrent"
                :key="index"
                class="flex items-center gap-3 flex-1 min-w-48 min-h-24 border-2 border-theme-border rounded-xl px-4 py-3 bg-theme-canvas"
            >
                <div class="flex-1 min-w-0">
                    <span
                        class="text-xs font-semibold uppercase tracking-widest text-theme-base"
                    >
                        Marca
                    </span>
                    <p
                        class="text-sm font-semibold text-theme-primary truncate mt-0.5"
                    >
                        {{ item.name }}
                    </p>
                    <span class="text-xs text-theme-muted font-mono">
                        {{ item.slug }}
                    </span>
                </div>
                <div class="shrink-0">
                    <app-button
                        v-if="enabled"
                        danger
                        :disabled="isLoadingComponent"
                        icon
                        @click="deleteBrand(index)"
                    >
                        🗑️
                    </app-button>
                </div>
            </div>

            <p
                v-if="!itemsCurrent.length"
                class="w-full text-sm text-theme-muted"
            >
                {{ emptyMessage }}
            </p>

            <!-- Card: Agregar marca -->
            <div
                v-if="enabled"
                class="flex items-center gap-3 flex-1 min-w-48 min-h-24 border-2 border-dashed border-theme-border rounded-xl px-4 py-3"
            >
                <div class="flex-1 min-w-0">
                    <span
                        class="text-xs font-semibold uppercase tracking-widest text-theme-muted"
                    >
                        Nueva marca
                    </span>
                </div>
                <button
                    type="button"
                    :disabled="isLoadingComponent"
                    class="shrink-0 flex items-center justify-center w-10 h-10 rounded-full hover:bg-theme-hover transition-colors cursor-pointer"
                    @click="openNewBrand"
                >
                    <span class="text-xl">➕</span>
                </button>
            </div>
        </div>

        <div v-if="enabled" class="flex my-4">
            <div class="w-full flex justify-center">
                <slot name="action"></slot>
            </div>
        </div>

        <div>
            <app-dialog v-model="isOpenSelector" size="lg">
                <template #header>
                    <div
                        class="flex items-center justify-between gap-4 px-6 py-4 border-b border-theme-border w-full"
                    >
                        <div>
                            <h3 class="text-lg font-bold text-theme-base">
                                🔖 Selecciona una marca
                            </h3>
                        </div>
                        <div class="flex items-center gap-2">
                            <app-button
                                :disabled="!selectedItems.length"
                                @click="pushNewBrand"
                            >
                                ✔️ Seleccionar ({{ selectedItems.length }})
                            </app-button>
                            <app-button
                                icon
                                circle
                                color="brand-purple"
                                @click="loadBrands(true)"
                            >
                                <reload-icon class="h-5 w-5 text-white" />
                            </app-button>
                        </div>
                    </div>
                </template>
                <div
                    v-if="items.length"
                    class="grid grid-col-1 sm:grid-cols-2 gap-2 px-4 py-3"
                >
                    <button
                        v-for="(item, index) in items"
                        :key="index"
                        class="w-60 text-left px-4 py-3 rounded-lg border-2 border-transparent bg-theme-card hover:bg-theme-hover transition-all cursor-pointer"
                        :class="{
                            'border-theme-primary bg-theme-primary text-white':
                                selectedItems.includes(item.slug)
                        }"
                        @click="selectionBrand(item.slug)"
                    >
                        <h3 class="text-theme-base font-medium">
                            {{ item.name }}
                        </h3>
                        <p class="text-sm text-theme-muted mt-1">
                            {{ item.summary }}
                        </p>
                    </button>
                </div>
                <div v-else class="px-4 py-3">
                    <span class="font-bold text-md">
                        Nuestro sistema no encuentra resultados 😢
                    </span>
                </div>
                <div
                    v-if="pagesNumber > currentPage"
                    class="flex justify-center mt-4 px-4 pb-3"
                >
                    <app-button @click="loadMoreBrands">
                        Cargar más
                    </app-button>
                </div>
            </app-dialog>
        </div>
    </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";

import ReloadIcon from "@/components/icons/ReloadIcon.vue";
import AppDialog from "@/components/utils/AppDialog.vue";

export default {
    name: "BrandsCurrentManage",
    components: { AppDialog, ReloadIcon },
    model: {
        prop: "itemsCurrent",
        event: "change"
    },
    props: {
        itemsCurrent: {
            default: () => [],
            type: Array,
            require: true
        },
        enabled: {
            default: () => true,
            type: Boolean,
            required: false
        },
        emptyMessage: {
            default: () => "Todavía no hay marcas destacadas.",
            type: String,
            required: false
        },
        defaultFilters: {
            default: () => ({
                active: true
            }),
            type: Object,
            required: false
        }
    },
    emits: ["change"],
    data: () => ({
        isOpenSelector: false,
        selectedItems: [],
        items: [],
        isLoadingComponent: false,
        currentPage: 1,
        pagesNumber: 0
    }),
    computed: {
        ...mapGetters("control", ["backgroundColor"])
    },
    watch: {
        itemsCurrent(value) {
            this.setInput(value);
        }
    },
    mounted() {
        this.setInput(this.itemsCurrent);
    },
    methods: {
        ...mapActions("brands", ["listBrandsPaginated"]),
        ...mapActions("products", ["updateProductsBrands"]),
        async openNewBrand() {
            this.isOpenSelector = true;
            await this.loadBrands();
        },
        async loadMoreBrands() {
            this.currentPage = this.currentPage + 1;
            await this.loadBrands();
        },
        async loadBrands(complete = false) {
            this.isLoadingComponent = true;
            try {
                const { items, pagination } = await this.listBrandsPaginated({
                    page: this.currentPage,
                    complete,
                    ...this.defaultFilters
                });
                this.items = items;
                if (pagination) {
                    this.pagesNumber = pagination.pagesNumber;
                }
                this.isLoadingComponent = false;
            } catch (error) {
                this.isLoadingComponent = false;
                this.isOpenSelector = false;
                this.$sound.play("error");
                this.$swal.fire({
                    background: this.backgroundColor,
                    title: error.title,
                    text: error.message,
                    icon: error.icon
                });
            }
        },
        selectionBrand(slug) {
            this.selectedItems.push(slug);
        },
        pushNewBrand() {
            let brandsAux = this.items.filter((i) =>
                this.selectedItems.includes(i.slug)
            );
            if (brandsAux.length) {
                const brandsResult = JSON.parse(
                    JSON.stringify(this.itemsCurrent)
                );

                brandsAux = [...brandsResult, ...brandsAux];

                brandsAux = brandsAux.filter(
                    (value, index, self) =>
                        self.findIndex((item) => item.slug === value.slug) ===
                        index
                );
                this.isOpenSelector = false;
                this.$emit("change", brandsAux);
            }
        },
        deleteBrand(fromIndex) {
            let brandsResult = [...this.itemsCurrent];
            brandsResult.splice(fromIndex, 1);
            this.$emit("change", brandsResult);
        },
        setInput(value) {
            this.selectedItems = value.map((item) => item.slug);
        }
    }
};
</script>

<style scoped>
.vs-card__group .vs-card__group-cards .vs-card-content {
    min-width: 15rem;
    margin-left: 20px;
}
</style>
